import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface TermsConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * TermsConsentCheckbox - Atomic component for Terms & Privacy consent
 *
 * This component displays a checkbox for users to accept Terms & Conditions
 * and Privacy Policy. It matches the style of the Perfios KYC consent component.
 *
 * @component
 * @example
 * ```tsx
 * <TermsConsentCheckbox
 *   checked={consent}
 *   onChange={setConsent}
 *   disabled={loading}
 * />
 * ```
 */
const TermsConsentCheckbox: React.FC<TermsConsentCheckboxProps> = ({
  checked,
  onChange,
  disabled = false,
}) => {
  return (
    <div className="flex items-start space-x-3">
      <Checkbox
        id="terms-consent"
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className="mt-1"
      />
      <div className="flex-1">
        <Label
          htmlFor="terms-consent"
          className="text-sm font-medium text-gray-900 cursor-pointer"
        >
          I accept the{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-600 hover:text-teal-700 underline"
            onClick={(e) => e.stopPropagation()}
          >
            Terms & Conditions
          </a>
          {' '}and{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-600 hover:text-teal-700 underline"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </a>
        </Label>
      </div>
    </div>
  );
};

export default TermsConsentCheckbox;
